import axios from 'axios';
import { normalizePartNumber } from './partNumber';

/** Normalize a store-code segment the same way the backend does (uppercase, no whitespace). */
function normalizeCodePart(value) {
  return String(value || '').trim().toUpperCase().replace(/\s+/g, '');
}

/** Build the inventory store code ({registration}__{partNumber}) that the backend keys rows by. */
export function buildStoreCodeKey(aircraftRegistration, partNumber) {
  const reg = normalizeCodePart(aircraftRegistration) || 'GEN';
  const pn = normalizeCodePart(partNumber);
  return pn ? `${reg}__${pn}` : '';
}

/** Store code of an inventory row (persisted, or reconstructed for legacy rows). */
function inventoryRowStoreCode(item) {
  const stored = normalizeCodePart(item?.storePartCode);
  if (stored) return stored;
  return buildStoreCodeKey(item?.aircraftRegistration || 'GEN', item?.partNumber);
}

/** Exact match on normalized part number within inventory search results. */
export function findExactInventoryMatch(items, partNumber) {
  const query = normalizePartNumber(partNumber).toLowerCase();
  if (!query) return null;
  const list = Array.isArray(items) ? items : [];
  return (
    list.find((item) => normalizePartNumber(item?.partNumber).toLowerCase() === query) || null
  );
}

/** Exact match on store code ({registration}__{partNumber}) within inventory search results. */
export function findInventoryMatchByStoreCode(items, storeCode) {
  const target = normalizeCodePart(storeCode);
  if (!target) return null;
  const list = Array.isArray(items) ? items : [];
  return list.find((item) => inventoryRowStoreCode(item) === target) || null;
}

/**
 * Look up an inventory row by store code (registration + part number).
 * Reuses the row the backend would create/receive into for the same aircraft context.
 * @param {string} partNumber
 * @param {{ aircraftRegistration?: string, signal?: AbortSignal }} options
 */
export async function fetchInventoryByStoreCode(partNumber, options = {}) {
  const storeCode = buildStoreCodeKey(options.aircraftRegistration, partNumber);
  if (!storeCode) return null;

  const response = await axios.get('/api/store/inventory', {
    params: {
      includeArchived: false,
      search: normalizePartNumber(partNumber),
      page: 1,
      pageSize: 50
    },
    signal: options.signal,
    skipGlobalErrorToast: true
  });
  const items = response.data?.items || response.data || [];
  return findInventoryMatchByStoreCode(items, storeCode);
}

/**
 * Search store inventory for a part number (regex API + client-side exact match).
 * @param {string} partNumber
 * @param {{ aircraftId?: string, signal?: AbortSignal }} options
 */
export async function fetchInventoryByPartNumber(partNumber, options = {}) {
  const normalized = normalizePartNumber(partNumber);
  if (!normalized) return null;

  const params = {
    includeArchived: false,
    search: normalized,
    page: 1,
    pageSize: 20
  };
  if (options.aircraftId) {
    params.aircraftId = options.aircraftId;
  }

  const response = await axios.get('/api/store/inventory', {
    params,
    signal: options.signal,
    skipGlobalErrorToast: true
  });
  const items = response.data?.items || response.data || [];
  return findExactInventoryMatch(items, normalized);
}

/** Shape an inventory row for part Autocomplete / PO line options. */
export function inventoryRowToPartOption(row) {
  if (!row?.partNumber) return null;
  return {
    _id: `inventory:${row._id}`,
    isInventoryItem: true,
    partNumber: normalizePartNumber(row.partNumber),
    partName: row.partName || '',
    description: String(row.description || '').trim(),
    unitMetric: row.unitMetric || 'EACH',
    category: row.category || 'PART',
    quantity: row.quantity,
    supplier: row.supplier
  };
}

/** Fields to autofill on procurement forms from an inventory row. */
export function inventoryAutofillFields(row) {
  if (!row) return {};
  const supplierId = row.supplier?._id || row.supplier || '';
  return {
    partNumber: normalizePartNumber(row.partNumber),
    description: String(row.description || '').trim(),
    unitMetric: row.unitMetric || 'EACH',
    category: row.category || 'PART',
    supplier: supplierId ? String(supplierId) : ''
  };
}
